import type { LabResultTemplateDocument } from '@type/lab_results.type'
import type {
  LabTestTemplateDefinition,
  LabTestTemplateDefinitionRow,
  LabTestTemplateFooter,
  LabTestTemplateHeaderField,
  LabTestTemplateResource,
  LabTestTemplateSection
} from '@type/labTestTep.type'
import { DEFAULT_LETTERHEAD_LAYOUT, DEFAULT_SIGNATURE_LAYOUT, normalizeReportBranding } from '@ui'
import { buildLabResultSignatureHtml } from '../labTemplateFooter'
import type {
  TemplateFormState,
  TemplateKeyValueRow,
  TemplateQuestion,
  TemplateSectionForm,
  TemplateTableColumn,
  TemplateTableRow
} from './types'

export const DEFAULT_FOOTER_CORRELATION =
  'This is a computer generated report and requires no signature. Please contact the laboratory for any clarifications.'
export const DEFAULT_FOOTER_SIGNOFF = 'Physician/Provider'

export function newId(): string {
  return crypto.randomUUID?.() ?? `t-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
}

export function columnHeaderToRowKey(header: string, colIndex = 0): string {
  const trimmed = header.trim()
  if (!trimmed) return `col_${colIndex}`

  const h = trimmed.toUpperCase()
  const map: Record<string, string> = {
    DRUG: 'drug',
    H: 'h',
    HINT: 'hint',
    O: 'o',
    ORGANISM: 'organism',
    PARAMETER: 'parameter',
    RANGE: 'range',
    REFERENCE: 'reference',
    RESULT: 'result',
    SENSITIVITY: 'sensitivity',
    TEST: 'test'
  }
  return map[h] ?? trimmed.toLowerCase().replace(/[^a-z0-9]+/g, '_')
}

export type PreviewTableRowCells = {
  id: string
  cells: string[]
}

export type DocTemplatePreviewTableSection = {
  preview_row_cells?: PreviewTableRowCells[]
} & import('@type/labTestTep.type').LabTestTemplateTableSection

function slugify(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

function createEmptyTableRow(columnCount: number): TemplateTableRow {
  const cells: Record<number, string> = {}
  const sampleCells: Record<number, string> = {}
  for (let i = 0; i < columnCount; i++) {
    cells[i] = ''
    sampleCells[i] = ''
  }
  return { id: newId(), cells, sampleCells }
}

export const DEFAULT_TABLE_ROWS = 3
export const DEFAULT_TABLE_COLUMNS = 3
export const MIN_TABLE_DIMENSION = 1
export const MAX_TABLE_DIMENSION = 50

export function clampTableDimension(value: number): number {
  if (!Number.isFinite(value)) return MIN_TABLE_DIMENSION
  return Math.min(MAX_TABLE_DIMENSION, Math.max(MIN_TABLE_DIMENSION, Math.trunc(value)))
}

function createDefaultTableColumns(count = DEFAULT_TABLE_COLUMNS): TemplateTableColumn[] {
  return Array.from({ length: count }, () => ({ header: '', mode: 'readonly' as const }))
}

function createDefaultTableRows(rowCount: number, columnCount: number): TemplateTableRow[] {
  return Array.from({ length: rowCount }, () => createEmptyTableRow(columnCount))
}

const DEFAULT_KEY_VALUE_ROWS = 3

function createDefaultKeyValueRows(count = DEFAULT_KEY_VALUE_ROWS): TemplateKeyValueRow[] {
  return Array.from({ length: count }, () => ({ id: newId(), label: '', sample: '' }))
}

export function createDefaultQuestion(type: TemplateQuestion['type'] = 'text'): TemplateQuestion {
  const base: TemplateQuestion = {
    id: newId(),
    question: '',
    type,
    sample: '',
    collapsed: false
  }

  if (type === 'table') {
    return {
      ...base,
      rows: DEFAULT_TABLE_ROWS,
      columns: DEFAULT_TABLE_COLUMNS,
      tableColumns: createDefaultTableColumns(DEFAULT_TABLE_COLUMNS),
      tableRows: createDefaultTableRows(DEFAULT_TABLE_ROWS, DEFAULT_TABLE_COLUMNS)
    }
  }

  if (type === 'dynamic_note' || type === 'static_note') {
    return { ...base, noteLabel: '', noteContent: '' }
  }

  if (type === 'key_value_list') {
    return { ...base, keyValueRows: createDefaultKeyValueRows() }
  }

  return base
}

export function createDefaultSection(): TemplateSectionForm {
  return {
    enabled: true,
    questions: [createDefaultQuestion('text')]
  }
}

export function createDefaultFormState(): TemplateFormState {
  return {
    title: '',
    category: '',
    letterhead: '',
    branding: {
      letterhead_layout: DEFAULT_LETTERHEAD_LAYOUT,
      signature_layout: DEFAULT_SIGNATURE_LAYOUT
    },
    header: { enabled: true, questions: [createDefaultQuestion('text')] },
    body: { enabled: true, questions: [createDefaultQuestion('table')] },
    footer: {
      enabled: true,
      questions: [{ ...createDefaultQuestion('signature'), question: DEFAULT_FOOTER_SIGNOFF }]
    }
  }
}

function resizeTableRows(rows: TemplateTableRow[], rowCount: number, columnCount: number): TemplateTableRow[] {
  const next = rows.slice(0, rowCount)
  while (next.length < rowCount) next.push(createEmptyTableRow(columnCount))
  return next.map(row => {
    const cells = { ...row.cells }
    const sampleCells = { ...row.sampleCells }
    for (let i = 0; i < columnCount; i++) {
      if (cells[i] === undefined) cells[i] = ''
      if (sampleCells[i] === undefined) sampleCells[i] = ''
    }
    return { ...row, cells, sampleCells }
  })
}

function resizeTableColumns(columns: TemplateTableColumn[], columnCount: number): TemplateTableColumn[] {
  const next = columns.slice(0, columnCount)
  while (next.length < columnCount) next.push({ header: '', mode: 'readonly' })
  return next
}

export function normalizeTableQuestion(question: TemplateQuestion): TemplateQuestion {
  if (question.type !== 'table') return question
  const rowCount = clampTableDimension(question.rows ?? DEFAULT_TABLE_ROWS)
  const columnCount = clampTableDimension(question.columns ?? DEFAULT_TABLE_COLUMNS)
  return {
    ...question,
    rows: rowCount,
    columns: columnCount,
    tableColumns: resizeTableColumns(question.tableColumns ?? createDefaultTableColumns(columnCount), columnCount),
    tableRows: resizeTableRows(
      question.tableRows ?? createDefaultTableRows(rowCount, columnCount),
      rowCount,
      columnCount
    )
  }
}

function buildTableSection(question: TemplateQuestion): LabTestTemplateSection | null {
  const q = normalizeTableQuestion(question)
  if (!q.tableColumns?.length || !q.tableRows?.length) return null

  const table_columns = q.tableColumns.map((col, idx) => col.header.trim() || `Column ${idx + 1}`)
  const editable_columns = q.tableColumns
    .map((col, idx) => ({
      key: columnHeaderToRowKey(col.header, idx),
      mode: col.mode
    }))
    .filter(item => item.mode === 'editable')
    .map(item => item.key)

  const rows: LabTestTemplateDefinitionRow[] = q.tableRows.map((row, index) => {
    const definitionRow: LabTestTemplateDefinitionRow = {
      id: row.id,
      sort_order: index + 1
    }
    const rec = definitionRow as unknown as Record<string, string>
    q.tableColumns!.forEach((col, colIdx) => {
      if (col.mode !== 'readonly') return
      rec[columnHeaderToRowKey(col.header, colIdx)] = row.cells[colIdx] ?? ''
    })
    return definitionRow
  })

  return {
    id: q.id,
    title: q.question.trim() || 'Table',
    presentation: 'table',
    table_columns,
    editable_columns,
    rows
  }
}

function buildNotesSection(question: TemplateQuestion): LabTestTemplateSection | null {
  if (question.type === 'static_note') {
    return {
      id: question.id,
      title: question.question.trim() || 'Note',
      presentation: 'notes',
      notes: {
        id: `${question.id}-notes`,
        kind: 'static_text',
        default_text: question.noteContent ?? ''
      }
    }
  }

  if (question.type === 'dynamic_note') {
    return {
      id: question.id,
      title: question.question.trim() || 'Note',
      presentation: 'notes',
      notes: {
        id: `${question.id}-notes`,
        kind: 'entry',
        label: question.noteLabel ?? '',
        placeholder: question.noteLabel ?? '',
        multiline: true,
        required: false
      }
    }
  }

  return null
}

function buildKeyValueListSection(question: TemplateQuestion): LabTestTemplateSection | null {
  if (question.type !== 'key_value_list') return null
  const rows = question.keyValueRows ?? []
  if (!rows.length) return null

  return {
    id: question.id,
    title: question.question.trim() || 'Key value list',
    presentation: 'key_value_list',
    rows: rows.map((row, index) => ({
      id: row.id,
      sort_order: index + 1,
      label: row.label.trim() || `Field ${index + 1}`
    }))
  }
}

function buildFooterFromQuestions(questions: TemplateQuestion[]): LabTestTemplateFooter {
  let correlation = DEFAULT_FOOTER_CORRELATION
  let signoff_role = DEFAULT_FOOTER_SIGNOFF
  let Stamp: string | undefined
  let stamp_signed_id: string | undefined

  for (const q of questions) {
    if (q.type === 'text' && q.question.trim()) {
      correlation = q.question.trim()
    }
    if (q.type === 'static_note' && q.noteContent?.trim()) {
      correlation = q.noteContent.trim()
    }
    if (q.type === 'signature' && q.question.trim()) {
      signoff_role = q.question.trim()
    }
  }

  return { correlation, signoff_role, Stamp, stamp_signed_id }
}

function buildSectionsFromQuestions(questions: TemplateQuestion[]): LabTestTemplateSection[] {
  const sections: LabTestTemplateSection[] = []
  for (const q of questions) {
    if (q.type === 'table') {
      const section = buildTableSection(q)
      if (section) sections.push(section)
      continue
    }
    if (q.type === 'dynamic_note' || q.type === 'static_note') {
      const section = buildNotesSection(q)
      if (section) sections.push(section)
      continue
    }
    if (q.type === 'key_value_list') {
      const section = buildKeyValueListSection(q)
      if (section) sections.push(section)
    }
  }
  return sections
}

export function formStateToDefinition(form: TemplateFormState): LabTestTemplateDefinition {
  const header_fields: LabTestTemplateHeaderField[] = []

  if (form.header.enabled) {
    for (const q of form.header.questions) {
      if (q.type !== 'text' && q.type !== 'date' && q.type !== 'time') continue
      header_fields.push({
        id: q.id,
        type: q.type,
        label: q.question.trim() || 'Field',
        required: false
      })
    }
  }

  const sections = form.body.enabled ? buildSectionsFromQuestions(form.body.questions) : []
  const footer = form.footer.enabled
    ? buildFooterFromQuestions(form.footer.questions)
    : { correlation: DEFAULT_FOOTER_CORRELATION, signoff_role: DEFAULT_FOOTER_SIGNOFF }

  return {
    footer,
    sections,
    letterhead: form.letterhead,
    header_fields,
    source_filename: `${slugify(form.title || 'template')}.json`,
    branding: normalizeReportBranding(form.branding)
  }
}

export function buildTemplatePayload(form: TemplateFormState, templateId?: string) {
  const definition = formStateToDefinition(form)
  const slug = slugify(form.title || 'template')
  const attributes = {
    name: form.title.trim(),
    category: form.category,
    active: true,
    definition,
    ...(templateId ? {} : { slug })
  }

  if (templateId) {
    return {
      data: {
        type: 'lab_test_template' as const,
        id: templateId,
        attributes
      }
    }
  }

  return {
    data: {
      type: 'lab_test_template' as const,
      attributes
    }
  }
}

function buildPreviewTableCells(question: TemplateQuestion): PreviewTableRowCells[] {
  const source = normalizeTableQuestion(question)
  return (source.tableRows ?? []).map(row => ({
    id: row.id,
    cells: (source.tableColumns ?? []).map((col, colIdx) => {
      const raw = col.mode === 'readonly' ? (row.cells[colIdx] ?? '') : (row.sampleCells[colIdx] ?? '')
      if (raw.trim()) return raw
      const key = columnHeaderToRowKey(col.header, colIdx)
      if (col.mode === 'editable' && key === 'result') return 'result'
      return ''
    })
  }))
}

function buildPreviewCellsFromSection(
  section: import('@type/labTestTep.type').LabTestTemplateTableSection
): PreviewTableRowCells[] {
  return [...section.rows]
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
    .map(row => ({
      id: row.id,
      cells: section.table_columns.map((col, colIdx) => {
        const key = columnHeaderToRowKey(col, colIdx)
        const raw = String((row as unknown as Record<string, unknown>)[key] ?? '')
        if (raw.trim()) return raw
        if (section.editable_columns.includes(key) && key === 'result') return 'result'
        return ''
      })
    }))
}

function tableSectionToQuestion(section: Extract<LabTestTemplateSection, { presentation: 'table' }>): TemplateQuestion {
  const tableColumns: TemplateTableColumn[] = section.table_columns.map((header, idx) => ({
    header,
    mode: (() => {
      const key = columnHeaderToRowKey(header, idx)
      return section.editable_columns.includes(key) ? 'editable' : 'readonly'
    })()
  }))

  const tableRows: TemplateTableRow[] = [...section.rows]
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
    .map(row => {
      const cells: Record<number, string> = {}
      const sampleCells: Record<number, string> = {}
      tableColumns.forEach((col, colIdx) => {
        const key = columnHeaderToRowKey(col.header, colIdx)
        const value = String((row as unknown as Record<string, unknown>)[key] ?? '')
        if (col.mode === 'readonly') cells[colIdx] = value
        else sampleCells[colIdx] = value || (key === 'result' ? 'result' : '')
      })
      return { id: row.id, cells, sampleCells }
    })

  return {
    id: section.id,
    question: section.title,
    type: 'table',
    rows: tableRows.length || DEFAULT_TABLE_ROWS,
    columns: tableColumns.length || DEFAULT_TABLE_COLUMNS,
    tableColumns,
    tableRows
  }
}

function notesSectionToQuestion(section: Extract<LabTestTemplateSection, { presentation: 'notes' }>): TemplateQuestion {
  const { notes } = section
  if (notes.kind === 'static_text') {
    return {
      id: section.id,
      question: section.title,
      type: 'static_note',
      noteContent: notes.default_text ?? ''
    }
  }
  return {
    id: section.id,
    question: section.title,
    type: 'dynamic_note',
    noteLabel: notes.label ?? '',
    noteContent: '',
    sample: ''
  }
}

function keyValueListSectionToQuestion(
  section: Extract<LabTestTemplateSection, { presentation: 'key_value_list' }>
): TemplateQuestion {
  return {
    id: section.id,
    question: section.title,
    type: 'key_value_list',
    keyValueRows: [...section.rows]
      .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
      .map(row => ({
        id: row.id,
        label: row.label ?? row.parameter ?? '',
        sample: row.value ?? ''
      }))
  }
}

export function resourceToFormState(resource: LabTestTemplateResource): TemplateFormState {
  const { definition, name, category } = resource.attributes
  const bodyQuestions: TemplateQuestion[] = definition.sections.map(section => {
    if (section.presentation === 'table') return tableSectionToQuestion(section)
    if (section.presentation === 'notes') return notesSectionToQuestion(section)
    if (section.presentation === 'key_value_list') return keyValueListSectionToQuestion(section)
    return createDefaultQuestion('text')
  })

  const headerQuestions: TemplateQuestion[] = definition.header_fields.length
    ? definition.header_fields.map(hf => ({
        id: hf.id,
        question: hf.label,
        type: (hf.type === 'date' || hf.type === 'time' ? hf.type : 'text') as 'text' | 'date' | 'time',
        sample: hf.value ?? ''
      }))
    : [createDefaultQuestion('text')]

  const footerQuestions: TemplateQuestion[] = [
    {
      id: newId(),
      question: definition.footer.signoff_role || DEFAULT_FOOTER_SIGNOFF,
      type: 'signature',
      sample: ''
    }
  ]

  const correlation = definition.footer.correlation
  if (correlation && correlation !== DEFAULT_FOOTER_CORRELATION) {
    footerQuestions.unshift({
      id: newId(),
      question: correlation,
      type: 'text',
      sample: ''
    })
  }

  return {
    title: name,
    category,
    letterhead: definition.letterhead ?? '',
    branding: normalizeReportBranding(definition.branding),
    header: { enabled: headerQuestions.length > 0, questions: headerQuestions },
    body: {
      enabled: bodyQuestions.length > 0,
      questions: bodyQuestions.length ? bodyQuestions : [createDefaultQuestion('table')]
    },
    footer: { enabled: true, questions: footerQuestions }
  }
}

export type DocTemplatePreviewDocument = LabResultTemplateDocument & { category: string }

export function formStateToPreviewDocument(form: TemplateFormState): DocTemplatePreviewDocument {
  const definition = formStateToDefinition(form)
  const previewDefinition = structuredClone(definition)

  if (form.header.enabled) {
    previewDefinition.header_fields = previewDefinition.header_fields.map(hf => {
      const q = form.header.questions.find(item => item.id === hf.id)
      return { ...hf, value: q?.sample?.trim() || 'NIL' }
    })
  }

  previewDefinition.sections = previewDefinition.sections.map(section => {
    const bodyQuestion = form.body.questions.find(q => q.id === section.id)
    if (!bodyQuestion) return section

    if (section.presentation === 'table' && bodyQuestion.type === 'table') {
      const source = normalizeTableQuestion(bodyQuestion)
      const preview_row_cells = buildPreviewTableCells(bodyQuestion)
      return {
        ...section,
        preview_row_cells,
        rows: section.rows.map((row, rowIndex) => {
          const sourceRow = source.tableRows?.[rowIndex]
          if (!sourceRow) return row
          const updated = { ...row } as LabTestTemplateDefinitionRow
          const rec = updated as unknown as Record<string, string>
          source.tableColumns?.forEach((col, colIdx) => {
            const key = columnHeaderToRowKey(col.header, colIdx)
            if (col.mode === 'readonly') rec[key] = sourceRow.cells[colIdx] ?? ''
            else rec[key] = sourceRow.sampleCells[colIdx]?.trim() || (key === 'result' ? 'result' : '')
          })
          return updated
        })
      } as DocTemplatePreviewTableSection
    }

    if (section.presentation === 'table') {
      return {
        ...section,
        preview_row_cells: buildPreviewCellsFromSection(section)
      } as DocTemplatePreviewTableSection
    }

    if (section.presentation === 'notes') {
      if (bodyQuestion.type === 'static_note') {
        return {
          ...section,
          notes: { ...section.notes, default_text: bodyQuestion.noteContent ?? '' }
        }
      }
      if (bodyQuestion.type === 'dynamic_note') {
        return {
          ...section,
          notes: {
            ...section.notes,
            value: bodyQuestion.sample?.trim() || bodyQuestion.noteContent?.trim() || '—'
          }
        }
      }
    }

    if (section.presentation === 'key_value_list' && bodyQuestion.type === 'key_value_list') {
      return {
        ...section,
        rows: section.rows.map((row, index) => {
          const sourceRow = bodyQuestion.keyValueRows?.[index]
          return {
            ...row,
            value: sourceRow?.sample?.trim() || '—'
          }
        })
      }
    }

    return section
  })

  const signatureQuestion = form.footer.questions.find(q => q.type === 'signature')
  if (signatureQuestion?.sample?.startsWith('data:image')) {
    previewDefinition.footer = {
      ...previewDefinition.footer,
      Signature: buildLabResultSignatureHtml(signatureQuestion.sample)
    }
  }

  return {
    slug: slugify(form.title || 'template'),
    name: form.title || 'Template preview',
    category: form.category,
    captured_at: new Date().toISOString(),
    footer: previewDefinition.footer,
    sections: previewDefinition.sections,
    letterhead: previewDefinition.letterhead,
    header_fields: previewDefinition.header_fields,
    branding: normalizeReportBranding(form.branding)
  }
}

export type DocTemplatePreviewLocationState = {
  from: 'builder' | 'list'
  form?: TemplateFormState
  templateId?: string
}
